import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Chip,
  IconButton,
  Pagination,
  MenuItem,
  Divider
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReactECharts from "echarts-for-react";
import moment from 'moment'
import Layout from "../components/DashboardLayout";
import { Helmet } from "react-helmet-async";
import DashboardHeader from "../components/Dashboard/DashboardHeader";
import KPICard from '../components/Orders/KPICard';
import { useNavigate } from "react-router-dom";

import ReservationsFilters from '../components/Orders/Filter'
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { getShipments, getStats } from "../api/shipments";
import { stat } from "fs";


const cellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/* ======================
   CHART CONFIG (STATIC)
====================== */
const chartOption = {
  tooltip: { trigger: "axis" },
  legend: {
    data: ["Mes pasado", "Mes actual"],
    right: 0,
    textStyle: { fontSize: 12, color: "#666" },
  },
  grid: { left: 24, right: 24, top: 40, bottom: 24 },
  xAxis: {
    type: "category",
    data: [
      
    ],
    axisLine: { show: false },
    axisTick: { show: false },
  },
  yAxis: {
    type: "value",
    splitLine: { lineStyle: { color: "#f0f0f0" } },
  },
  series: [
    {
      name: "Mes pasado",
      type: "bar",
      data: [],
      barWidth: 10,
      itemStyle: { color: "#1f2aff", borderRadius: 6 },
    },
    {
      name: "Mes actual",
      type: "bar",
      data: [],
      barWidth: 10,
      itemStyle: { color: "#6ee7d8", borderRadius: 6 },
    },
  ],
};

type OrderStatus =
  | ""
  | "delivered"
  | "cancelled"
  | "created"
  | "confirmed"
  | "in_transit"
  | "ending"
  ;

/* ======================
   PAGE
====================== */
export default function ProductsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [community, setCommunity] = useState("");
  const [status, setStatus] = useState<OrderStatus>("");


  const [stats, setStats] = useState<any>(null);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  const today = new Date();

  const [chartData, setChartData] = useState<{
    labels: string[];
    current: number[];
    previous: number[];
  } | null>(null);

  const fetchVisitsChart = async () => {
    try {
        // const res = await getReservationsChart();
        // if (res.data.status) {
        // setChartData(res.data.data);
        // }
    } catch (e) {
        console.error(e);
    }
  };


  /* ======================
     FETCH VISITS
  ====================== */

  const fetchStats = async () => {
      try {

        const res = await getStats();
  
        if (res.data.status) {
          setStats(res.data.data);
        }
      } catch (err) {
        
      } finally {
        
      }
    };
  
    


  const fetchShipments = async () => {
    try {
      setLoading(true);
      const res = await getShipments();
      if (res.data.status) {
        setRows(res.data.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
    fetchStats();

  }, []);


  

  /* ======================
    FILTERS
    ====================== */
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      // 🔍 Búsqueda libre en code, customer, phone, payment, total, notes
      if (search) {
        const s = search.toLowerCase();
        if (
          !(
            r.tracking_code?.toLowerCase().includes(s) ||
            r.receiver?.toLowerCase().includes(s) ||
            r.driver?.toLowerCase().includes(s) ||
            r.vehicle?.toLowerCase().includes(s) ||
            r.current_status?.toString().includes(s) 
          )
        ) return false;
      }

      // 🏘 Distrito
      if (community && r.geozone_name !== community) return false;

      // 🚦 Estado
      if (status && r.status_code !== status) return false;

      // 📅 Rango de fechas
      const reservationDate = moment(r.transferDate, "YYYY-MM-DD HH:mm");
      if (from) {
        const fromDate = moment(from, "YYYY-MM-DD").startOf("day");
        if (!reservationDate.isValid() || reservationDate.isBefore(fromDate))
          return false;
      }
      if (to) {
        const toDate = moment(to, "YYYY-MM-DD").endOf("day");
        if (!reservationDate.isValid() || reservationDate.isAfter(toDate))
          return false;
      }


      return true;
    });
  }, [rows, search, community, status, from, to]);


  const exportToExcel = () => {
    const data = filteredRows.map((r) => ({
      Códico: r.tracking_code,
      Cliente: r.receiver,
      Partida: r.startAddress,
      Llegada: r.arrivalAddress,
      Vehículo: r.vehicle,
      Conductor: r.driver,
      FechaEmisión: moment.utc(r.transferDate).format("DD/MM/YYYY"),
      FechaEstimada: r.estimatedDeliveryDate,
      Estado: getTypeLabel(r.status_code).label,
      MotivoCancelación: getCancelReasonLabel(r.reason),
      
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Pedidos"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      fileData,
      `Pedidos_${moment().format("YYYYMMDD_HHmm")}.xlsx`
    );
  };




  const paginated = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const communities = Array.from(
    new Set(rows.map((r) => r.geozone_name).filter(Boolean))
  );

  const getTypeLabel = (status: string) => {
    switch (status) {

      case 'created':
        return { label : 'Creado', textColor : '#fff' as const, color: '#818181' as const }
        break;
      case 'confirmed':
        return { label : 'Rocojo Confirmado', textColor : '#fff' as const, color: '#4863f9' as const }
        break;
      case 'in_transit':
        return { label : 'En tránsito', textColor : '#fff' as const, color: '#673AB7' as const }
        break;
      case 'ending':
        return { label : 'En ruta final', textColor : '#fff' as const, color: '#007ce1' as const }
        break;
      case 'delivered':
        return { label : 'Entregado', textColor : '#080000' as const, color: '#9cfb58' as const }
        break;
      case 'cancelled':
        return { label : 'Cancelado', textColor : '#fff' as const, color: '#ff4343' as const }
        break;
           
      default:
        return { label: "Pendiente", textColor : '#000' as const, color: "#FFDE4D" as const };        
    }
  };

  const cancelReasons = [
    { value: "cliente_no_responde", label: "El cliente no responde" },
    { value: "error_en_orden", label: "Error en la orden" },
    { value: "solicitud_cliente", label: "Cancelado por el cliente" },
    { value: "otro", label: "Otro motivo" },
  ];

  const getCancelReasonLabel = (reason?: string | null): string => {
    if (!reason) return "-";
    const found = cancelReasons.find((r) => r.value === reason);
    return found ? found.label : reason;
  };

  useEffect(() => {
    setPage(1);
  }, [search, community, status, from, to]);

  /* ======================
     RENDER
  ====================== */
  return (
    <Layout>
      <Helmet>
        <title>Rodaris - Órdenes</title>
      </Helmet>

      <DashboardHeader title="Pedidos" />

      <Box sx={{ p: 4 }}>

        {/* ------------- KPIs ARRIBA ------------- */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={2}>
              <KPICard
                label="Creado"
                value={stats?.created?.toString() ?? "0"}
              />
            </Grid>
  
            <Grid item xs={12} sm={6} md={2}>
              <KPICard
                label="Confirmado"
                value={stats?.confirmed?.toString() ?? "0"}
              />
            </Grid>
  
            <Grid item xs={12} sm={6} md={2}>
              <KPICard
                label="En transito"
                value={stats?.in_transit?.toString() ?? "0"}
              />
            </Grid>
  
            <Grid item xs={12} sm={6} md={2}>
              <KPICard
                label="En ruta final"
                value={stats?.ending?.toString() ?? "0"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <KPICard
                label="Entregado"
                value={stats?.delivered?.toString() ?? "0"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <KPICard
                label="Cancelado"
                value={stats?.cancelled?.toString() ?? "0"}
              />
            </Grid>
            
          </Grid>


          

        <Grid container spacing={4}>
          {/* ======================
              CARD: GRÁFICO
          ====================== */}
          

          {/* ======================
              CARD: TABLA
          ====================== */}

          <Grid item xs={12}>
            <ReservationsFilters
                search={search}
                setSearch={setSearch}
                community={community}
                setCommunity={setCommunity}
                status={status}
                setStatus={setStatus}
                from={from}
                setFrom={setFrom}
                to={to}
                setTo={setTo}
                communities={communities}
                onReset={() => {
                    setSearch("");
                    setCommunity("");
                    setStatus("");
                    setFrom("");
                    setTo("");
                    setPage(1);
                }}
                onExport={exportToExcel}
            />


          </Grid>



          <Grid item xs={12}>
            <Paper
                elevation={0}
                sx={{
                borderRadius: 3,
                border: "1px solid #f0f0f0",
                overflow: "hidden",
                width: "100%",
                }}
            >
                

                {/* ======================
                    TABLE HEADER
                ====================== */}
                <Box
                    sx={{
                        display: "grid",
                        width: "100%",
                        gridTemplateColumns:
                        "1fr 3fr 2.5fr 2.5fr 1.2fr 2fr 1.3fr 1.3fr 1.8fr 1.8fr",
                        px: 3,
                        py: 1.5,
                        fontSize: 11.5,
                        fontWeight: 500,
                        backgroundColor: "#fafafa",
                    }}
                    >
                    <span>Códico</span>
                    <span>Cliente</span>
                    <span>Partida</span>
                    <span>Llegada</span>
                    <span>Vehículo</span>
                    <span>Conductor</span>                    
                    <span>Fecha Emisión</span>
                    <span>Fecha Estimada</span>
                    <span>Estado</span>
                    <span>Motivo Cancelación</span>
                    
                    
                </Box>


                {/* ======================
                    ROWS
                ====================== */}
                {paginated.map((r) => {
                    const status = getTypeLabel(r.status_code);

                    return (
                        <Box
                        key={r.id}
                        sx={{
                            display: "grid",
                            width: "100%",
                            gridTemplateColumns:
                            "1fr 3fr 2.5fr 2.5fr 1.2fr 2fr 1.3fr 1.3fr 1.8fr 1.8fr",
                            px: 3,
                            py: 1.5,
                            fontSize: 12,
                            borderTop: "1px solid #f2f2f2",
                            alignItems: "center",
                        }}
                        >
                        <span
                          style={{
                              ...cellStyle,
                              cursor: "pointer",
                              color: "#1976d2",
                          }}
                          // onClick={() => navigate(`/pedidos/${r.id}`)}
                          >
                          {r.tracking_code}
                          </span>
                        <span style={cellStyle}>{r.receiver}</span>
                        <span style={cellStyle}>{r.startAddress}</span>
                        <span style={cellStyle}>{r.arrivalAddress}</span>
                        <span style={cellStyle}>{r.vehicle}</span>                        
                        <span style={cellStyle}>{r.driver}</span>
                        <span style={cellStyle}>
                            {moment.utc(r.transferDate).format("DD/MM/YYYY")}
                        </span>
                        <span style={cellStyle}>
                            {moment.utc(r.estimatedDeliveryDate).format("DD/MM/YYYY")}
                        </span>
                        <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              marginRight: 2,
                            backgroundColor : status.color,
                            color: status.textColor,
                            borderRadius: 1,
                            fontSize: 11,
                            fontWeight: 500,
                            height: 22,
                            "& .MuiChip-label": { px: 1 },
                            }}
                        />
                        <span style={cellStyle}>{getCancelReasonLabel(r.reason)}</span>
                        </Box>
                    );
                })}


                {/* ======================
                    PAGINATION
                ====================== */}
                <Box
                sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    borderTop: "1px solid #f2f2f2",
                }}
                >
                <Pagination
                    count={Math.ceil(filteredRows.length / rowsPerPage)}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    size="small"
                />
                </Box>
            </Paper>
            </Grid>

        </Grid>
      </Box>
    </Layout>
  );
}
